import classNames from 'classnames'
import { format } from 'date-fns'
import { Clock, TriangleAlert } from 'lucide-react'
import { AddToCalendar } from 'src/modules/booking/components/add-to-calendar'
import { Button, Icon, Typography } from 'src/modules/core/components'
import { BookingLogo } from '../booking-logo'
import { PoweredBy } from '../powered-by'
import type { BookingConfirmedStepProps } from './booking-confirmed-step.interface'
import classes from './booking-confirmed-step.module.scss'
import { useBookingConfirmedStep } from './use-booking-confirmed-step'

export function BookingConfirmedStep(props: BookingConfirmedStepProps) {
  const v = useBookingConfirmedStep(props)

  if (v.isLoading) {
    return (
      <div className={classes.container}>
        <div className={classes.root}>
          <div className={classes.loadingMessage}>
            <Typography variant="body">Loading booking details...</Typography>
          </div>
        </div>
        <PoweredBy className={classes.poweredBy} />
      </div>
    )
  }

  if (v.showNotFound) {
    return (
      <div className={classes.container}>
        <div className={classes.root}>
          <div className={classes.errorCard}>
            <div className={classes.errorIcon}>
              <Icon name="error" />
            </div>
            <Typography variant="h1" className={classes.errorTitle}>
              Unable to locate booking
            </Typography>
            <Typography variant="body" className={classes.errorMessage}>
              This link may be expired, incorrect, or the appointment has been cancelled. Please check the link or
              contact the provider.
            </Typography>
          </div>
        </div>
        <PoweredBy className={classes.poweredBy} />
      </div>
    )
  }

  const { displayData, providerName, appointmentType, calendarEventData, locationRow, timeRangeLabel } = v

  return (
    <div className={classes.container}>
      <div className={classes.root}>
        <div className={classes.header}>
          <div className={classes.headerIcon}>
            <Icon name="checkmark" />
          </div>
          <Typography variant="h1" className={classes.title}>
            Booking Confirmed! 🎉
          </Typography>
        </div>

        <div className={classes.contentCard}>
          <BookingLogo logoUrl={v.logoUrl} alt={providerName} fallback={null} className={classes.cardLogo} />
          {displayData.selectedDate && displayData.selectedTime && (
            <div className={classes.detailsBox}>
              <Typography variant="body" className={classes.detailsTitle}>
                Appointment Details
              </Typography>
              {appointmentType && (
                <div className={classes.detailItem}>
                  <Typography variant="body" className={classes.detailText}>
                    {appointmentType.name} ({appointmentType.duration_minutes} min)
                  </Typography>
                </div>
              )}
              <div className={classes.detailItem}>
                <Typography variant="body" className={classes.detailLabel}>
                  With
                </Typography>
                <div className={classes.detailValue}>
                  <Typography variant="body" className={classes.detailText}>
                    {providerName}
                  </Typography>
                </div>
              </div>
              <div className={classes.detailItem}>
                <Icon name="calendarAlt" className={classes.detailIcon} />
                <Typography variant="body" className={classes.detailText}>
                  {format(displayData.selectedDate, 'EEEE, MMMM d')}
                </Typography>
              </div>
              <div className={classes.detailItem}>
                <Icon name="clock" className={classes.detailIcon} />
                <Typography variant="body" className={classes.detailText}>
                  {timeRangeLabel}
                </Typography>
              </div>
              {locationRow && (
                <div className={classes.detailItem} data-testid="booking-location-row">
                  <Icon name="pin" className={classes.detailIcon} />
                  <div className={classes.locationValue}>
                    {locationRow.label && (
                      <Typography variant="body" className={classes.detailText}>
                        {locationRow.label}
                      </Typography>
                    )}
                    {locationRow.href ? (
                      <a
                        href={locationRow.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classNames(classes.link, classes.locationLink)}
                      >
                        {locationRow.text}
                      </a>
                    ) : (
                      <Typography variant="body" className={classes.detailText}>
                        {locationRow.text}
                      </Typography>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {v.cancellationPolicy && (
            <div className={classes.policySection} data-testid="booking-cancellation-policy">
              <Typography variant="body" className={classes.sectionTitle}>
                Cancellation Policy
              </Typography>
              <div className={classes.policyCard}>
                {v.cancellationPolicy.short_notice ? (
                  <div className={classNames(classes.policyLine, classes.policyShortNotice)}>
                    <TriangleAlert size={20} className={classes.policyLineIcon} aria-hidden="true" />
                    <span>This appointment is already inside the cancellation window.</span>
                  </div>
                ) : (
                  <div className={classes.policyLine}>
                    <Clock size={20} className={classes.policyLineIcon} aria-hidden="true" />
                    <span>
                      Cancel by <strong>{v.cancelByLabel}</strong>
                    </span>
                  </div>
                )}
                <Typography variant="body" className={classes.policyText}>
                  {v.cancellationPolicy.text}
                </Typography>
              </div>
            </div>
          )}

          <div className={classes.whatsNext}>
            <Typography variant="body" className={classes.sectionTitle}>
              What&apos;s Next?
            </Typography>
            <ul className={classes.instructionsList}>
              <li>
                <Typography variant="body" className={classes.instructionText}>
                  The appointment has been added to the provider&apos;s calendar
                </Typography>
              </li>
              {v.showEmailOnItsWay && (
                <li>
                  <Typography variant="body" className={classes.instructionText}>
                    A confirmation email is on its way to the email address you provided
                  </Typography>
                </li>
              )}
              {!v.canReschedule && (
                <li>
                  <Typography variant="body" className={classes.instructionText}>
                    If you need to reschedule, please contact <span>{providerName}</span> directly
                  </Typography>
                </li>
              )}
              <li>
                <Typography variant="body" className={classes.instructionText}>
                  Please save this confirmation and arrive on time
                </Typography>
              </li>
            </ul>
          </div>

          <div className={classes.calendarSection}>
            <AddToCalendar calendarEventData={calendarEventData} />
          </div>

          {v.canReschedule && (
            <div className={classes.rescheduleSection}>
              <Typography variant="body" className={classes.sectionTitle}>
                Need to reschedule?
              </Typography>
              <Typography variant="body" className={classes.rescheduleText}>
                Click{' '}
                <a href={`/bookings/${props.bookingId}`} className={classes.link}>
                  here
                </a>{' '}
                to pick a new slot.
              </Typography>
            </div>
          )}

          <div className={classes.actions}>
            <div className={classes.ctaSection}>
              <Typography variant="body" className={classes.ctaText}>
                Want to book clients like this?
              </Typography>
              <Button className={classes.ctaButton} onClick={v.handleCtaClick}>
                Start Accepting Bookings
              </Button>
            </div>
          </div>
        </div>
      </div>
      <PoweredBy className={classes.poweredBy} />
    </div>
  )
}
